import { apiClient } from "shared/api/client";

/** 공지사항(notices) 도메인 API. */

// 홈 대시보드 등에서 쓰는 단순 목록 조회 (데이터 반환)
export const getNotices = async () => {
  const response = await apiClient.get("/notices");
  return response.data;
};

// 특정 공지글 불러오기 (setStateCb로 상태 주입 + 데이터 반환)
export const fetchNotice = async (setStateCb, noticeId) => {
  const response = await apiClient.get(`/admin/noticeKorEng/${noticeId}`);
  if (response.status !== 200) {
    throw new Error();
  }
  setStateCb(response.data);
  return response.data;
};

// 모든 공지글 불러오기
export const fetchNotices = async (setStateCb) => {
  const response = await apiClient.get("/notices/");
  if (response.status !== 200) {
    throw new Error();
  }
  setStateCb(response.data);
  return response.data;
};

// 새로고침
export const reloadNotices = async (setStateCb) => {
  return await fetchNotices(setStateCb);
};

// 공지글 업데이트
export const updateNotice = async (targetId, fieldsToUpdate) => {
  const endpoint = `/notices/${targetId}`;
  const normalizedTitles = Array.isArray(fieldsToUpdate?.titles)
    ? fieldsToUpdate.titles
    : Array.isArray(fieldsToUpdate?.fileTitle)
    ? fieldsToUpdate.fileTitle
    : [];
  const normalizedUrls = Array.isArray(fieldsToUpdate?.urls)
    ? fieldsToUpdate.urls
    : Array.isArray(fieldsToUpdate?.fileURL)
    ? fieldsToUpdate.fileURL
    : [];
  // 서버 필드명 변경: urls → fileKeys, image → imageKey (값은 모두 S3 key).
  const payload = {
    ...fieldsToUpdate,
    titles: normalizedTitles,
    fileKeys: normalizedUrls,
  };
  delete payload.urls;
  delete payload.fileURL;
  if (Object.prototype.hasOwnProperty.call(payload, "image")) {
    payload.imageKey = payload.image;
    delete payload.image;
  }

  const response = await apiClient.patch(endpoint, payload);
  if (response.status >= 200 && response.status < 300) {
    return targetId;
  }
  throw new Error(`failed id: ${targetId}`);
};

// 공지글 가리기/복구 (클라이언트의 삭제/복구 기능)
// 서버는 PATCH /notices/{단일 id}만 받으므로 다중 선택은 id별로 개별 요청한다.
export const setNoticeToHide = async (ids, shouldHide) => {
  const idList = Array.isArray(ids) ? ids : [ids];
  await Promise.all(idList.map((id) => updateNotice(id, { hide: shouldHide })));
};

// 공지글 고정/고정 해제
// 서버는 PATCH /notices/{단일 id}만 받으므로 다중 선택은 id별로 개별 요청한다.
export const setNoticeToPin = async (ids, shouldPin) => {
  const idList = Array.isArray(ids) ? ids : [ids];
  await Promise.all(idList.map((id) => updateNotice(id, { pin: shouldPin })));
};

// 공지글 추가
export const createNotice = async (fields) => {
  const endpoint = "/notices/";
  const normalizedTitles = Array.isArray(fields.titles)
    ? fields.titles
    : Array.isArray(fields.fileTitle)
    ? fields.fileTitle
    : [];
  const normalizedUrls = Array.isArray(fields.urls)
    ? fields.urls
    : Array.isArray(fields.fileURL)
    ? fields.fileURL
    : [];
  // 서버 필드명 변경: urls → fileKeys, image → imageKey (값은 모두 S3 key).
  const payload = {
    titles: normalizedTitles,
    fileKeys: normalizedUrls,
    title: fields.title ?? "",
    imageKey: fields.image ?? "",
    content: fields.content ?? "",
    pin: Boolean(fields.pin),
    engTitle: fields.engTitle ?? "",
    engContent: fields.engContent ?? "",
  };

  try {
    const response = await apiClient.post(endpoint, payload);
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    throw new Error(`공지 생성 요청 실패(status=${response.status})`);
  } catch (error) {
    const responseData = error?.response?.data ?? null;
    const status = error?.response?.status || "NO_STATUS";
    const path = responseData?.path || endpoint;
    const payloadSummary = {
      userId: null,
      titlesLength: Array.isArray(payload.titles) ? payload.titles.length : 0,
      fileKeysLength: Array.isArray(payload.fileKeys)
        ? payload.fileKeys.length
        : 0,
      hasTitlesField: Object.prototype.hasOwnProperty.call(payload, "titles"),
      hasFileKeysField: Object.prototype.hasOwnProperty.call(
        payload,
        "fileKeys",
      ),
      titleLength: String(payload.title ?? "").length,
      contentLength: String(payload.content ?? "").length,
      engTitleLength: String(payload.engTitle ?? "").length,
      engContentLength: String(payload.engContent ?? "").length,
      hasImage: Boolean(payload.imageKey),
      pin: Boolean(payload.pin),
    };
    throw new Error(
      `공지 생성 요청 실패: ${JSON.stringify({
        attempt: 1,
        status,
        path,
        responseData,
        payloadSummary,
      })}`,
    );
  }
};
